<ng-container
  *transloco="
    let t;
    scope: 'featureSearch';
    prefix: 'featureSearch.searchResults'
  "
>
  @if (dialogProperties$ | async; as dialogProperties) {
    <bd-dialog
      data-id="manage-object-columns-dialog"
      [visible]="visible()"
      [draggable]="false"
      [dialogProperties]="dialogProperties"
      extraStyleClass="bd-manage-columns-dialog"
      (dismiss)="onDismiss()"
      (confirm)="onConfirm()"
    >
      <div class="bd-manage-columns-dialog-panels">
        <section
          class="bd-manage-columns-panel"
          aria-labelledby="available-columns-title"
        >
          <div class="bd-manage-columns-panel-header">
            <div class="bd-manage-columns-panel-label">
              <h3
                id="available-columns-title"
                aria-describedby="available-columns-info"
              >
                {{ t('availableColumns') }}
              </h3>
              <span id="available-columns-info" class="sr-only">{{
                t('availableColumnsInfo')
              }}</span>
              <i
                class="ui-info"
                aria-hidden="true"
                [pTooltip]="t('availableColumnsInfo')"
              ></i>
            </div>
            <p-button
              data-id="available-columns-search-button"
              [ariaLabel]="t('searchColumns')"
              [text]="true"
              icon="ui-search"
              [styleClass]="
                availableColumnsSearchActive()
                  ? 'bd-neutral bd-active'
                  : 'bd-neutral'
              "
              (onClick)="toggleAvailableColumnsSearch()"
            ></p-button>
          </div>

          @if (availableColumnsSearchActive()) {
            <div class="bd-manage-columns-search">
              <p-iconfield class="bd-size-small">
                <p-inputicon class="ui-search" />
                <input
                  data-id="available-columns-search-input"
                  pInputText
                  class="bd-manage-columns-search-input bd-size-small"
                  [ngModel]="availableColumnsSearchTerm()"
                  [placeholder]="t('searchColumnsPlaceholder')"
                  [attr.aria-label]="t('searchColumns')"
                  (ngModelChange)="searchAvailableColumns($event)"
                />
              </p-iconfield>
            </div>
          }

          <div class="bd-manage-columns-groups">
            @for (group of availableColumnGroups(); track group.key) {
              @let groupName =
                group.kind === 'system'
                  ? t('systemCategory')
                  : group.categoryName;
              <div class="bd-manage-columns-category-header">
                <p-checkbox
                  [attr.data-id]="'column-group-checkbox-' + group.key"
                  [binary]="true"
                  [ngModel]="group.allSelected"
                  [indeterminate]="group.indeterminate"
                  [ariaLabel]="
                    t('toggleColumnCategory', { categoryName: groupName })
                  "
                  checkboxIcon="ui-check"
                  (ngModelChange)="toggleColumnGroup(group, $event)"
                ></p-checkbox>
                <span class="bd-manage-columns-category-name">{{
                  groupName
                }}</span>
                <span class="bd-manage-columns-category-count">
                  {{ group.selectedColumnCount }}/{{ group.columns.length }}
                </span>
                <button
                  type="button"
                  [attr.data-id]="'column-group-toggle-' + group.key"
                  class="bd-manage-columns-category-toggle"
                  [attr.aria-label]="
                    t('toggleColumnCategoryExpanded', {
                      categoryName: groupName,
                    })
                  "
                  [attr.aria-expanded]="group.expanded"
                  (click)="toggleColumnGroupExpanded(group.key)"
                >
                  <i
                    [class.ui-up]="group.expanded"
                    [class.ui-down]="!group.expanded"
                    aria-hidden="true"
                  ></i>
                </button>
              </div>

              @if (group.expanded) {
                <div class="bd-manage-columns-list">
                  @for (column of group.filteredColumns; track column.id) {
                    @let availableColumnLabel =
                      column.kind === 'system'
                        ? t(column.headerKey)
                        : column.label;
                    <label
                      class="bd-manage-columns-available-row"
                      [class.bd-manage-columns-available-row-readonly]="
                        !column.canHide
                      "
                      [for]="'available-column-checkbox-' + column.id"
                    >
                      <p-checkbox
                        [attr.data-id]="
                          'available-column-checkbox-' + column.id
                        "
                        [inputId]="'available-column-checkbox-' + column.id"
                        [binary]="true"
                        [ngModel]="isColumnVisible(column.id)"
                        [readonly]="!column.canHide"
                        [ariaLabel]="availableColumnLabel"
                        checkboxIcon="ui-check"
                        (ngModelChange)="
                          onAvailableColumnChange(column.id, $event)
                        "
                      ></p-checkbox>
                      <span
                        [innerHTML]="
                          availableColumnLabel
                            | highlight: availableColumnsSearchTerm().trim()
                        "
                      ></span>
                    </label>
                  }
                </div>
              }
            }
            @if (
              availableColumnsSearchActive() && !hasMatchingAvailableColumns()
            ) {
              <div
                data-id="available-columns-no-results"
                class="bd-manage-columns-no-results"
              >
                {{ t('noMatchingColumns') }}
              </div>
            }
          </div>
        </section>

        <section
          class="bd-manage-columns-panel"
          aria-labelledby="selected-columns-title"
        >
          <div class="bd-manage-columns-panel-header">
            <h3 id="selected-columns-title">{{ t('selectedColumns') }}</h3>
            <p-button
              data-id="selected-columns-search-button"
              [ariaLabel]="t('searchColumns')"
              [text]="true"
              icon="ui-search"
              [styleClass]="
                selectedColumnsSearchActive()
                  ? 'bd-neutral bd-active'
                  : 'bd-neutral'
              "
              (onClick)="toggleSelectedColumnsSearch()"
            ></p-button>
          </div>

          @if (selectedColumnsSearchActive()) {
            <div class="bd-manage-columns-search">
              <p-iconfield class="bd-size-small">
                <p-inputicon class="ui-search" />
                <input
                  data-id="selected-columns-search-input"
                  pInputText
                  class="bd-manage-columns-search-input bd-size-small"
                  [ngModel]="selectedColumnsSearchTerm()"
                  [placeholder]="t('searchColumnsPlaceholder')"
                  [attr.aria-label]="t('searchColumns')"
                  (ngModelChange)="selectedColumnsSearchTerm.set($event)"
                />
              </p-iconfield>
            </div>
          }

          <div class="bd-manage-columns-selected-list">
            @for (group of selectedColumnGroups(); track group.key) {
              @if (group.columns.length > 0) {
                <div
                  class="bd-manage-columns-selected-group"
                  cdkDropList
                  [cdkDropListData]="group.columns"
                  (cdkDropListDropped)="
                    onSelectedColumnsDrop($event, group.key)
                  "
                >
                  @if (group.filteredColumns.length > 0) {
                    <div class="bd-manage-columns-group-label">
                      {{ t(group.labelKey) }}
                    </div>
                    @for (column of group.filteredColumns; track column.id) {
                      @let selectedColumnLabel =
                        column.kind === 'system'
                          ? t(column.headerKey)
                          : column.label;
                      @let selectedColumnMenuItems =
                        selectedColumnMenuItemsById().get(column.id) ?? []
                          | translateMenuItems
                          | async;
                      <div
                        class="bd-manage-columns-selected-row"
                        cdkDrag
                        [attr.data-id]="'selected-column-row-' + column.id"
                      >
                        <i
                          class="ui-grip-horizontal bd-manage-columns-drag-handle"
                          cdkDragHandle
                          aria-hidden="true"
                        ></i>
                        <span
                          class="bd-manage-columns-selected-label"
                          [innerHTML]="
                            selectedColumnLabel
                              | highlight: selectedColumnsSearchTerm().trim()
                          "
                        ></span>
                        <span class="bd-manage-columns-selected-source">{{
                          column.kind === 'system'
                            ? t('fromSystem')
                            : t('fromCategory', {
                                categoryName: column.categoryName,
                              })
                        }}</span>
                        <p-button
                          [attr.data-id]="
                            'selected-column-menu-button-' + column.id
                          "
                          [ariaLabel]="t('columnActionsBtnInfo')"
                          [text]="true"
                          icon="ui-ellipsis-vertical"
                          styleClass="bd-neutral"
                          (onClick)="
                            onSelectedColumnMenuClick(
                              $event,
                              selectedColumnMenu
                            )
                          "
                        ></p-button>
                        <p-tiered-menu
                          #selectedColumnMenu
                          styleClass="bd-action-menu bd-toolbar-menu bd-more-menu bd-small"
                          appendTo="body"
                          [popup]="true"
                          [autoDisplay]="false"
                          [model]="selectedColumnMenuItems"
                        >
                          <ng-template
                            pTemplate="item"
                            let-menuItem
                            let-hasSubmenu="hasSubmenu"
                          >
                            <bd-menu-item
                              [item]="menuItem"
                              [hasSubmenu]="hasSubmenu"
                              (itemClick)="
                                onMenuItemClick($event.event, menuItem)
                              "
                            />
                          </ng-template>
                        </p-tiered-menu>
                      </div>
                    }
                  }
                </div>
              }
            }
            @if (
              selectedColumnsSearchActive() &&
              filteredPinnedColumns().length === 0 &&
              filteredUnpinnedColumns().length === 0
            ) {
              <div
                data-id="selected-columns-no-results"
                class="bd-manage-columns-no-results"
              >
                {{ t('noMatchingColumns') }}
              </div>
            }
          </div>
        </section>
      </div>
    </bd-dialog>
  }
</ng-container>
